/* ==========================================================================
   Kintsugi — motion engine

   One module owns every scroll effect in the theme. That matters for two
   reasons specific to Shopify:

   1. The theme editor fires shopify:section:load / :unload every time a
      merchant edits a section. Anything that registers a ScrollTrigger without
      tearing the old one down leaks triggers and double-animates. Every
      behaviour here registers into a per-section registry that is destroyed on
      unload.
   2. ScrollTrigger must own the scroll position exclusively. This is why the
      theme ships no second scroll library — two rAF loops fighting over scroll
      is what makes iOS Safari stutter.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.documentElement;

  var Motion = {
    level: root.dataset.motion || 'full',
    ready: false,
    behaviours: {},
    // section id -> array of teardown functions
    registry: new Map()
  };

  Motion.enabled = function () {
    return Motion.level !== 'off';
  };
  Motion.full = function () {
    return Motion.level === 'full';
  };

  var hasGsap = typeof window.gsap !== 'undefined';
  var hasScrollTrigger = hasGsap && typeof window.ScrollTrigger !== 'undefined';

  if (hasScrollTrigger) {
    window.gsap.registerPlugin(window.ScrollTrigger);
    if (typeof window.SplitText !== 'undefined') {
      window.gsap.registerPlugin(window.SplitText);
    }
    // Sticky/pinned sections need a refresh after webfonts land, or the pin
    // distances are computed against fallback-font metrics and drift.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        window.ScrollTrigger.refresh();
      });
    }
  }

  /* ----------------------------------------------------------------------
     Registration plumbing
     ---------------------------------------------------------------------- */

  Motion.define = function (name, fn) {
    Motion.behaviours[name] = fn;
  };

  function sectionKey(el) {
    var section = el.closest('[data-section-id]');
    return section ? section.dataset.sectionId : '__page__';
  }

  function addTeardown(el, fn) {
    var key = sectionKey(el);
    if (!Motion.registry.has(key)) Motion.registry.set(key, []);
    Motion.registry.get(key).push(fn);
  }

  Motion.mount = function (scope) {
    scope = scope || document;
    var nodes = scope.querySelectorAll('[data-motion-behaviour]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      // Track which behaviours have run per element, not just "this element was
      // visited". Behaviour modules (kintsugi.js) load independently and may
      // register after the first mount pass; a single boolean would strand them.
      if (!el.__motionMounted) el.__motionMounted = {};
      var names = (el.dataset.motionBehaviour || '').split(/\s+/);
      for (var j = 0; j < names.length; j++) {
        var name = names[j];
        if (!name || el.__motionMounted[name]) continue;
        var fn = Motion.behaviours[name];
        if (!fn) continue; // module not loaded yet; a later mount pass gets it
        el.__motionMounted[name] = true;
        try {
          var teardown = fn(el);
          if (typeof teardown === 'function') addTeardown(el, teardown);
        } catch (e) {
          // A broken effect must never take the page with it.
          if (window.console) console.warn('[motion] ' + name + ' failed', e);
        }
      }
    }
  };

  Motion.unmount = function (key) {
    var list = Motion.registry.get(key);
    if (!list) return;
    for (var i = 0; i < list.length; i++) {
      try { list[i](); } catch (e) {}
    }
    Motion.registry.delete(key);
  };

  /* ----------------------------------------------------------------------
     reveal — the workhorse. Fade + rise on enter, fade + sink on exit, in
     both scroll directions. This is the piece that makes sections feel like
     one continuous experience instead of a stack of static cards: a
     component that only ever enters and never leaves reads as a page of
     popups, not a place the reader moves through.

     `reduced` keeps enter-only (once) on purpose — reversing motion when the
     reader has asked for less of it is exactly the annoyance that setting
     exists to prevent.
     ---------------------------------------------------------------------- */

  Motion.define('reveal', function (el) {
    var targets = el.hasAttribute('data-reveal')
      ? [el]
      : el.querySelectorAll('[data-reveal]');

    if (!targets.length) return;

    // Motion off: CSS already forces these visible. Nothing to do.
    if (!Motion.enabled()) return;

    if (!hasScrollTrigger) {
      // No GSAP: reveal immediately rather than leave content at opacity 0.
      for (var i = 0; i < targets.length; i++) targets[i].setAttribute('data-revealed', '');
      return;
    }

    var gsap = window.gsap;

    if (!Motion.full()) {
      var reducedSt = window.ScrollTrigger.create({
        trigger: el,
        start: 'top 85%',
        once: true,
        onEnter: function () {
          for (var i = 0; i < targets.length; i++) targets[i].setAttribute('data-revealed', '');
        }
      });
      return function () { reducedSt.kill(); };
    }

    var stagger = parseFloat(el.dataset.revealStagger || '0.08');
    var tween = gsap.to(targets, {
      opacity: 1,
      y: 0,
      duration: 0.9,
      ease: 'power3.out',
      stagger: stagger,
      scrollTrigger: {
        trigger: el,
        start: 'top 85%',
        end: 'bottom 12%',
        toggleActions: 'play reverse play reverse'
      },
      onStart: function () {
        for (var i = 0; i < targets.length; i++) targets[i].setAttribute('data-revealed', '');
      },
      onReverseComplete: function () {
        for (var i = 0; i < targets.length; i++) targets[i].removeAttribute('data-revealed');
      }
    });

    return function () {
      if (tween.scrollTrigger) tween.scrollTrigger.kill();
      tween.kill();
    };
  });

  /* ----------------------------------------------------------------------
     split-lines — line-by-line type reveal for headings and pull quotes.
     ---------------------------------------------------------------------- */

  Motion.define('split-lines', function (el) {
    if (!Motion.full() || !hasScrollTrigger || typeof window.SplitText === 'undefined') {
      el.setAttribute('data-revealed', '');
      return;
    }

    var killed = false;
    var cleanup = null;

    function build() {
      if (killed) return;

      var gsap = window.gsap;
      var split = new window.SplitText(el, { type: 'lines', linesClass: 'split-line' });

      // Each line needs its own clipping context to mask the rise.
      for (var i = 0; i < split.lines.length; i++) {
        split.lines[i].style.overflow = 'hidden';
        split.lines[i].style.paddingBottom = '0.08em';
      }

      var inner = gsap.utils.toArray(split.lines).map(function (line) {
        var span = document.createElement('span');
        span.style.display = 'block';
        while (line.firstChild) span.appendChild(line.firstChild);
        line.appendChild(span);
        return span;
      });

      el.setAttribute('data-revealed', '');

      var tl = gsap.from(inner, {
        yPercent: 115,
        duration: 1.1,
        ease: 'power4.out',
        stagger: 0.075,
        scrollTrigger: {
          trigger: el,
          start: 'top 88%',
          end: 'bottom 12%',
          toggleActions: 'play reverse play reverse'
        }
      });

      cleanup = function () {
        if (tl.scrollTrigger) tl.scrollTrigger.kill();
        tl.kill();
        split.revert();
      };
    }

    // SplitText measures line boxes to decide where to cut. Splitting before the
    // webfont arrives measures fallback-font metrics, and when the real face
    // swaps in the lines reflow against wrappers that were sized for the wrong
    // font — masked lines then clip or overlap. GSAP warns about exactly this.
    // The element carries no [data-reveal], so it stays visible while we wait;
    // there is no flash of hidden text.
    if (document.fonts && document.fonts.status !== 'loaded') {
      document.fonts.ready.then(build);
    } else {
      build();
    }

    return function () {
      killed = true;
      if (cleanup) cleanup();
    };
  });

  /* ----------------------------------------------------------------------
     kintsugi-rule — a gold crack travels the hairline as it scrolls past.
     Pure CSS custom-property drive, so it costs one style recalc per frame.
     ---------------------------------------------------------------------- */

  Motion.define('kintsugi-rule', function (el) {
    if (!Motion.full() || !hasScrollTrigger) {
      el.style.setProperty('--crack', '100%');
      return;
    }

    var st = window.ScrollTrigger.create({
      trigger: el,
      start: 'top 92%',
      end: 'top 45%',
      scrub: true,
      onUpdate: function (self) {
        el.style.setProperty('--crack', (self.progress * 100).toFixed(2) + '%');
      }
    });

    return function () { st.kill(); };
  });

  /* ----------------------------------------------------------------------
     river — the chapter spine. Fills a gradient as you descend and lights
     each chapter dot as it is reached.
     ---------------------------------------------------------------------- */

  Motion.define('river', function (el) {
    var items = el.querySelectorAll('.river__item');

    if (!Motion.enabled() || !hasScrollTrigger) {
      el.style.setProperty('--flow', '100%');
      for (var i = 0; i < items.length; i++) items[i].dataset.seen = 'true';
      return;
    }

    var triggers = [];

    var flow = window.ScrollTrigger.create({
      trigger: el,
      start: 'top 60%',
      end: 'bottom 75%',
      scrub: true,
      onUpdate: function (self) {
        el.style.setProperty('--flow', (self.progress * 100).toFixed(2) + '%');
      }
    });
    triggers.push(flow);

    Array.prototype.forEach.call(items, function (item) {
      triggers.push(
        window.ScrollTrigger.create({
          trigger: item,
          start: 'top 72%',
          onEnter: function () { item.dataset.seen = 'true'; },
          onLeaveBack: function () { item.dataset.seen = 'false'; }
        })
      );
    });

    return function () {
      triggers.forEach(function (t) { t.kill(); });
    };
  });

  /* ----------------------------------------------------------------------
     water-current — una corriente de luz de agua fluye por encima de una
     cita. El flujo constante es CSS (@keyframes quote-flow); aquí solo
     enganchamos el scroll: --current 0..1 sube la intensidad al atravesar la
     sección. Sin animación/scroll deja --current en un valor medio para que
     igual se vea la lámina.
     ---------------------------------------------------------------------- */
  /* ----------------------------------------------------------------------
     wave-hero — la portada emerge del agua cristalina al hacer scroll.
     --reveal 0.3 (sumergida) → 1 (fuera). Sin scroll queda a 0.6 para que
     nunca se vea un bloque de agua sin portada.
     ---------------------------------------------------------------------- */
  Motion.define('wave-hero', function (el) {
    if (!Motion.enabled() || !hasScrollTrigger) {
      el.style.setProperty('--reveal', '1');
      return;
    }
    el.style.setProperty('--reveal', '0.3');
    var st = window.ScrollTrigger.create({
      trigger: el,
      start: 'top top',
      end: 'bottom top',
      scrub: true,
      onUpdate: function (self) {
        // Arranca a 0.3 (parte de la portada ya fuera) y termina fuera (1).
        el.style.setProperty('--reveal', (0.3 + 0.7 * self.progress).toFixed(3));
      }
    });
    return function () { st.kill(); };
  });

  Motion.define('water-current', function (el) {
    if (!Motion.enabled() || !hasScrollTrigger) {
      el.style.setProperty('--current', '0.5');
      return;
    }
    var st = window.ScrollTrigger.create({
      trigger: el,
      start: 'top bottom',
      end: 'bottom top',
      scrub: true,
      onUpdate: function (self) {
        // Campana: máxima intensidad cuando la cita está centrada.
        var p = 1 - Math.abs(self.progress - 0.5) * 2;
        el.style.setProperty('--current', p.toFixed(3));
      }
    });
    return function () { st.kill(); };
  });

  /* ----------------------------------------------------------------------
     parallax — subtle depth on media. Capped hard: anything past ~12% of
     viewport height reads as sliding, not depth.
     ---------------------------------------------------------------------- */

  Motion.define('parallax', function (el) {
    if (!Motion.full() || !hasScrollTrigger) return;

    var depth = Math.min(parseFloat(el.dataset.parallaxDepth || '0.12'), 0.2);
    var tween = window.gsap.fromTo(
      el,
      { yPercent: -depth * 50 },
      {
        yPercent: depth * 50,
        ease: 'none',
        scrollTrigger: {
          trigger: el.parentElement || el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true
        }
      }
    );

    return function () {
      if (tween.scrollTrigger) tween.scrollTrigger.kill();
      tween.kill();
    };
  });

  /* ----------------------------------------------------------------------
     sequence — scroll-scrubbed image sequence.

     The honest cost: a frame set is the single heaviest thing on the page.
     Mitigations, all of them necessary rather than nice-to-have:
       - Frame count and width are chosen per breakpoint, so a phone never
         downloads the desktop set.
       - Frames decode through createImageBitmap off the main thread where
         available, so scrubbing does not block on decode.
       - Loading is bounded by a small concurrency window and starts with a
         coarse pass (every Nth frame) so something is paintable early.
       - Nothing loads until the section is near the viewport.
     ---------------------------------------------------------------------- */

  Motion.define('sequence', function (el) {
    var canvas = el.querySelector('.sequence__canvas');
    if (!canvas) return;

    // Any reason the scrub cannot run collapses the section to its still frame.
    // CSS keys the fallback off data-static, so no blank canvas is ever shown.
    if (!Motion.full() || !hasScrollTrigger) {
      el.setAttribute('data-static', 'true');
      return;
    }

    var isMobile = window.matchMedia('(max-width: 749px)').matches;

    // A phone gets its own lighter frame set when one is configured.
    var pattern = isMobile
      ? el.dataset.seqPatternMobile || el.dataset.seqPattern
      : el.dataset.seqPattern;

    // Misconfigured or unusable: fall back to the still rather than pinning
    // three screens of scroll onto an empty canvas.
    function bail() {
      el.setAttribute('data-static', 'true');
    }

    if (!pattern || pattern.indexOf('{i}') === -1) return bail();

    var total = parseInt(isMobile ? el.dataset.seqCountMobile : el.dataset.seqCount, 10);
    if (!total || total < 2) return bail();

    var width = parseInt(isMobile ? el.dataset.seqWidthMobile : el.dataset.seqWidth, 10) || 1280;
    var pad = parseInt(el.dataset.seqPad || '4', 10);
    var start = parseInt(el.dataset.seqStart || '1', 10);

    var ctx = canvas.getContext('2d', { alpha: false });
    var frames = new Array(total);
    var loaded = 0;
    var current = -1;
    var destroyed = false;

    function frameUrl(i) {
      var n = String(start + i);
      while (n.length < pad) n = '0' + n;
      return pattern.replace('{i}', n).replace('{w}', String(width));
    }

    function size() {
      // Cap DPR at 2. A 3x phone gains nothing visible here and pays 2.25x
      // the fill cost every scrubbed frame.
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      draw(current < 0 ? 0 : current, true);
    }

    function draw(index, force) {
      if (destroyed) return;
      if (index === current && !force) return;
      var img = frames[index];
      if (!img) {
        // Hold the nearest already-decoded frame rather than flashing blank.
        var nearest = nearestLoaded(index);
        if (nearest === -1) return;
        img = frames[nearest];
      }
      current = index;

      var cw = canvas.width;
      var ch = canvas.height;
      if (!cw || !ch) return;
      var iw = img.width;
      var ih = img.height;
      // contain
      var scale = Math.min(cw / iw, ch / ih);
      var dw = iw * scale;
      var dh = ih * scale;
      ctx.clearRect(0, 0, cw, ch);
      ctx.drawImage(img, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
    }

    function nearestLoaded(index) {
      for (var d = 1; d < total; d++) {
        if (frames[index - d]) return index - d;
        if (frames[index + d]) return index + d;
      }
      return -1;
    }

    function decode(url) {
      // createImageBitmap keeps decode off the main thread. Fall back to Image
      // where it is missing (older Safari) — still correct, just less smooth.
      if (window.createImageBitmap && window.fetch) {
        return fetch(url, { mode: 'cors', credentials: 'omit' })
          .then(function (r) {
            if (!r.ok) throw new Error('HTTP ' + r.status);
            return r.blob();
          })
          .then(function (b) { return window.createImageBitmap(b); });
      }
      return new Promise(function (resolve, reject) {
        var img = new Image();
        img.decoding = 'async';
        img.onload = function () { resolve(img); };
        img.onerror = reject;
        img.src = url;
      });
    }

    var queue = [];
    // Coarse pass first (every 4th frame), then fill the gaps. The sequence
    // becomes scrubbable long before it is complete.
    for (var step = 4; step >= 1; step = step >> 1) {
      for (var i = 0; i < total; i += step) {
        if (queue.indexOf(i) === -1) queue.push(i);
      }
      if (step === 1) break;
    }

    var inFlight = 0;
    var MAX_PARALLEL = 6;

    function pump() {
      if (destroyed) return;
      while (inFlight < MAX_PARALLEL && queue.length) {
        var index = queue.shift();
        if (frames[index]) continue;
        inFlight++;
        /* eslint-disable no-loop-func */
        (function (idx) {
          decode(frameUrl(idx))
            .then(function (bitmap) {
              if (destroyed) {
                if (bitmap.close) bitmap.close();
                return;
              }
              frames[idx] = bitmap;
              loaded++;
              if (loaded === 1) {
                size();
                el.setAttribute('data-seq-ready', 'true');
              } else if (idx === current) {
                draw(idx, true);
              }
            })
            .catch(function () { /* a missing frame just holds the previous one */ })
            .then(function () {
              inFlight--;
              pump();
            });
        })(index);
        /* eslint-enable no-loop-func */
      }
    }

    // Do not touch the network until the section is close.
    var giveUp = 0;
    var loadTrigger = window.ScrollTrigger.create({
      trigger: el,
      start: 'top bottom+=100%',
      once: true,
      onEnter: function () {
        pump();
        // If a wrong URL pattern means nothing ever decodes, stop pretending
        // and show the still. Without this the reader gets a blank pinned pane.
        giveUp = setTimeout(function () {
          if (!destroyed && loaded === 0) bail();
        }, 8000);
      }
    });

    // Default range assumes a tall div (--seq-scroll, e.g. 300svh): scrolling
    // from its top to its bottom hitting the viewport bottom IS the runway.
    // A short instance (the hero, ~1 viewport tall) needs the OTHER
    // convention instead — 'bottom top', the full natural scroll-through —
    // or that range collapses to near-zero and frames blow through in a
    // couple of scroll ticks. Opt in per-instance via data-seq-trigger-end;
    // book-sequence.liquid never sets it, so it keeps 'bottom bottom'.
    var triggerEnd = el.dataset.seqTriggerEnd || 'bottom bottom';

    // 0.35s of catch-up easing reads fine over book-sequence's 300svh runway,
    // but over a short range (the hero) that same lag is proportionally huge:
    // it looks sluggish AND keeps drifting for a beat after scroll stops.
    // scrub:true ties the frame 1:1 to scroll position, zero lag. Opt in via
    // data-seq-scrub="true"; book-sequence never sets it, keeps 0.35.
    var scrubVal = el.dataset.seqScrub === 'true' ? true : 0.35;

    var proxy = { frame: 0 };
    var scrubTween = window.gsap.to(proxy, {
      frame: total - 1,
      ease: 'none',
      scrollTrigger: {
        trigger: el,
        start: 'top top',
        end: triggerEnd,
        scrub: scrubVal,
        pin: el.querySelector('.sequence__stage') ? false : true,
        invalidateOnRefresh: true
      },
      onUpdate: function () {
        draw(Math.round(proxy.frame));
      }
    });

    var onResize = window.gsap.utils
      ? debounce(size, 150)
      : size;
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    size();

    return function () {
      destroyed = true;
      clearTimeout(giveUp);
      loadTrigger.kill();
      if (scrubTween.scrollTrigger) scrubTween.scrollTrigger.kill();
      scrubTween.kill();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      // Release GPU-backed bitmaps explicitly; the GC will not do it promptly.
      for (var i = 0; i < frames.length; i++) {
        if (frames[i] && frames[i].close) frames[i].close();
        frames[i] = null;
      }
    };
  });

  function debounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, wait);
    };
  }

  /* ----------------------------------------------------------------------
     velocity-skew — the whole page leans slightly into the scroll direction
     and settles. One transform on one container per frame, so it is the
     cheapest "expensive-looking" effect in the theme. Capped at 4deg: past
     that it reads as a rendering fault rather than momentum.
     ---------------------------------------------------------------------- */

  Motion.define('velocity-skew', function (el) {
    if (!Motion.full() || !hasScrollTrigger) return;
    // Gate en touch removida (2026-09-08): "lo mismo que sale en desktop
    // sale en mobile" — el mismo riesgo que ya se aceptó para h-scroll/
    // pin-scenes. Revertir = volver a poner el guard hover:hover de abajo.
    // if (!window.matchMedia('(hover: hover)').matches) return;

    var gsap = window.gsap;
    var max = Math.min(parseFloat(el.dataset.skewMax || '3'), 4);
    var setSkew = gsap.quickTo(el, 'skewY', { duration: 0.5, ease: 'power3.out' });

    var st = window.ScrollTrigger.create({
      onUpdate: function (self) {
        var v = gsap.utils.clamp(-max, max, self.getVelocity() / -260);
        setSkew(v);
      }
    });

    // Skew needs a rendering context of its own or it drags fixed children.
    el.style.transformOrigin = 'center center';
    el.style.willChange = 'transform';

    return function () {
      st.kill();
      gsap.set(el, { skewY: 0, clearProps: 'willChange,transformOrigin' });
    };
  });

  /* ----------------------------------------------------------------------
     h-scroll — pinned horizontal gallery.

     Forced on touch too, at the author's explicit request (2026-09-08) —
     accepted risk: pinning the page to drive a horizontal track can read as
     the scroll "getting stuck" under a swipe on some phones. If that report
     comes back, the fix is to restore the `touch` guard below (native
     scroll-snap carousel), not to patch around the symptom.
     ---------------------------------------------------------------------- */

  Motion.define('h-scroll', function (el) {
    var track = el.querySelector('[data-h-track]');
    if (!track) return;

    if (!Motion.full() || !hasScrollTrigger) {
      el.setAttribute('data-h-native', 'true');
      return;
    }

    var gsap = window.gsap;
    var tween = gsap.to(track, {
      x: function () {
        // Recomputed on every refresh so it survives font loading and resize.
        return -(track.scrollWidth - el.offsetWidth);
      },
      ease: 'none',
      scrollTrigger: {
        trigger: el,
        pin: true,
        scrub: 0.4,
        // Scroll distance equals the overflow, so the pace matches the content.
        end: function () {
          return '+=' + (track.scrollWidth - el.offsetWidth);
        },
        invalidateOnRefresh: true,
        anticipatePin: 1
      }
    });

    return function () {
      if (tween.scrollTrigger) tween.scrollTrigger.kill();
      tween.kill();
      gsap.set(track, { x: 0 });
    };
  });

  /* ----------------------------------------------------------------------
     sticky-stack — cards pin in place and the next one slides over, each
     earlier card shrinking slightly. Reads as depth without any blur or
     shadow work.
     ---------------------------------------------------------------------- */

  Motion.define('sticky-stack', function (el) {
    var cards = el.querySelectorAll('[data-stack-card]');
    if (cards.length < 2) return;
    if (!Motion.full() || !hasScrollTrigger) return;

    var gsap = window.gsap;
    var triggers = [];

    Array.prototype.forEach.call(cards, function (card, i) {
      // The last card has nothing stacking on top of it.
      if (i === cards.length - 1) return;
      var tween = gsap.to(card, {
        scale: 0.94,
        opacity: 0.45,
        ease: 'none',
        scrollTrigger: {
          trigger: cards[i + 1],
          start: 'top bottom',
          end: 'top top',
          scrub: true
        }
      });
      triggers.push(tween);
    });

    return function () {
      triggers.forEach(function (t) {
        if (t.scrollTrigger) t.scrollTrigger.kill();
        t.kill();
      });
      gsap.set(cards, { clearProps: 'scale,opacity' });
    };
  });

  /* ----------------------------------------------------------------------
     count-up — a number rolls to its value once, on enter.
     ---------------------------------------------------------------------- */

  Motion.define('count-up', function (el) {
    var target = parseFloat(el.dataset.countTo || el.textContent.replace(/[^\d.-]/g, ''));
    if (isNaN(target)) return;

    var suffix = el.dataset.countSuffix || '';
    var prefix = el.dataset.countPrefix || '';

    function render(v) {
      // es-CO grouping: 619, 1.200 — matches how the rest of the page reads.
      el.textContent = prefix + Math.round(v).toLocaleString('es-CO') + suffix;
    }

    if (!Motion.enabled() || !hasScrollTrigger) {
      render(target);
      return;
    }

    render(0);
    var proxy = { v: 0 };
    var tween = window.gsap.to(proxy, {
      v: target,
      duration: 1.6,
      ease: 'power2.out',
      onUpdate: function () { render(proxy.v); },
      scrollTrigger: { trigger: el, start: 'top 88%', once: true }
    });

    return function () {
      if (tween.scrollTrigger) tween.scrollTrigger.kill();
      tween.kill();
      render(target);
    };
  });

  /* ----------------------------------------------------------------------
     marquee — a seamless ribbon that speeds up and reverses with the scroll.
     ---------------------------------------------------------------------- */

  Motion.define('marquee', function (el) {
    var track = el.querySelector('[data-marquee-track]');
    if (!track) return;

    // En teléfono la cinta se apila (ver --marquee-mode en base.css) para que
    // el verso completo quepa. Apilado no hay loop que montar y duplicar el
    // contenido mostraría cada verso dos veces, así que se sale antes de tocar
    // el DOM. El CSS es la única fuente de verdad del breakpoint.
    if ((getComputedStyle(el).getPropertyValue('--marquee-mode') || '').trim() === 'stack') return;

    var gsap = window.gsap;
    // Duplicate the content so the loop has no visible seam.
    var original = track.innerHTML;
    track.innerHTML = original + original;

    if (!Motion.full() || !hasGsap) return;

    var half = track.scrollWidth / 2;
    var speed = parseFloat(el.dataset.marqueeSpeed || '28');

    var loop = gsap.to(track, {
      x: -half,
      duration: half / speed,
      ease: 'none',
      repeat: -1,
      modifiers: {
        x: function (x) { return (parseFloat(x) % half) + 'px'; }
      }
    });

    var st = null;
    if (hasScrollTrigger) {
      st = window.ScrollTrigger.create({
        onUpdate: function (self) {
          // Scroll direction drives the ribbon direction; velocity drives pace.
          loop.timeScale(self.direction * gsap.utils.clamp(1, 5, Math.abs(self.getVelocity()) / 380 + 1));
        }
      });
      // Do not burn frames animating a ribbon nobody can see.
      window.ScrollTrigger.create({
        trigger: el,
        start: 'top bottom',
        end: 'bottom top',
        onToggle: function (self) { self.isActive ? loop.play() : loop.pause(); }
      });
    }

    return function () {
      if (st) st.kill();
      loop.kill();
      track.innerHTML = original;
    };
  });

  /* ----------------------------------------------------------------------
     clip-reveal — the image uncovers itself instead of fading in. Uses
     clip-path inset, which the compositor handles without repainting.
     ---------------------------------------------------------------------- */

  Motion.define('clip-reveal', function (el) {
    if (!Motion.full() || !hasScrollTrigger) return;

    var from = el.dataset.clipFrom || 'bottom';
    var insets = {
      bottom: 'inset(100% 0% 0% 0%)',
      top: 'inset(0% 0% 100% 0%)',
      left: 'inset(0% 100% 0% 0%)',
      right: 'inset(0% 0% 0% 100%)'
    };

    var tween = window.gsap.fromTo(
      el,
      { clipPath: insets[from] || insets.bottom },
      {
        clipPath: 'inset(0% 0% 0% 0%)',
        duration: 1.3,
        ease: 'power3.inOut',
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
          end: 'bottom 12%',
          toggleActions: 'play reverse play reverse'
        }
      }
    );

    return function () {
      if (tween.scrollTrigger) tween.scrollTrigger.kill();
      tween.kill();
      window.gsap.set(el, { clearProps: 'clipPath' });
    };
  });

  /* ----------------------------------------------------------------------
     progress-bar — how far through the page the reader is.

     This is a retention device, not decoration: people finish things whose end
     they can see. It writes one CSS custom property per frame and nothing else.
     ---------------------------------------------------------------------- */

  Motion.define('progress-bar', function (el) {
    if (!Motion.enabled()) return;

    if (!hasScrollTrigger) {
      // Cheap native fallback so the bar still works without GSAP.
      var onScroll = function () {
        var max = document.documentElement.scrollHeight - window.innerHeight;
        el.style.setProperty('--progress', max > 0 ? (window.scrollY / max) : 0);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      return function () { window.removeEventListener('scroll', onScroll); };
    }

    var st = window.ScrollTrigger.create({
      start: 0,
      end: 'max',
      onUpdate: function (self) {
        el.style.setProperty('--progress', self.progress.toFixed(4));
      }
    });

    return function () { st.kill(); };
  });

  /* ----------------------------------------------------------------------
     chapter-tracker — a fixed marker showing which chapter the reader is in.

     Progress plus a curiosity gap: seeing "III / XII" tells you there is more
     and roughly how much, which is what keeps people going.
     ---------------------------------------------------------------------- */

  Motion.define('chapter-tracker', function (el) {
    var targetSelector = el.dataset.trackTargets || '.river__item';
    var items = document.querySelectorAll(targetSelector);
    if (!items.length) return;

    var currentEl = el.querySelector('[data-track-current]');
    var posEl = el.querySelector('[data-track-pos]');
    var totalEl = el.querySelector('[data-track-total]');
    var labelEl = el.querySelector('[data-track-label]');

    // The fraction counts positions in this list, never the chapter numeral.
    // The merchant's blocks are a selection — "II / 6" would claim the book has
    // six chapters when it has twelve.
    if (totalEl) totalEl.textContent = items.length;

    if (!Motion.enabled() || !hasScrollTrigger) {
      el.setAttribute('hidden', '');
      return;
    }

    var triggers = [];
    var shown = -1;

    function show(i) {
      if (i === shown) return;
      shown = i;
      var item = items[i];
      if (currentEl) {
        currentEl.textContent =
          (item.dataset.trackNumeral || '') !== '' ? item.dataset.trackNumeral : i + 1;
      }
      if (posEl) posEl.textContent = i + 1;
      if (labelEl) labelEl.textContent = item.dataset.trackLabel || '';
    }

    // One trigger driving a positional scan, not one window per item. Per-item
    // start/end windows do not tile: a chapter shorter than 20% of the viewport
    // gets an inverted range and never toggles, which left the marker stuck on
    // an earlier chapter while later ones were on screen.
    function pick() {
      var line = window.innerHeight * 0.55;
      var idx = 0;
      for (var i = 0; i < items.length; i++) {
        if (items[i].getBoundingClientRect().top <= line) idx = i;
      }
      show(idx);
    }

    triggers.push(
      window.ScrollTrigger.create({
        trigger: items[0],
        endTrigger: items[items.length - 1],
        start: 'top 70%',
        end: 'bottom 30%',
        onUpdate: pick,
        onToggle: function (self) {
          el.dataset.visible = String(self.isActive);
          if (self.isActive) pick();
        }
      })
    );

    show(0);

    return function () {
      triggers.forEach(function (t) { t.kill(); });
    };
  });

  /* ----------------------------------------------------------------------
     parallax-scene — layered depth. Each [data-depth] child moves at its own
     rate, so the scene has real separation instead of one sliding image.
     Depth is clamped: past ~25% of the container the layers visibly detach.
     ---------------------------------------------------------------------- */

  Motion.define('parallax-scene', function (el) {
    if (!Motion.full() || !hasScrollTrigger) return;

    var layers = el.querySelectorAll('[data-depth]');
    if (!layers.length) return;

    var gsap = window.gsap;
    var tweens = [];

    Array.prototype.forEach.call(layers, function (layer) {
      var depth = gsap.utils.clamp(0, 0.25, parseFloat(layer.dataset.depth) || 0.1);
      tweens.push(
        gsap.fromTo(
          layer,
          { yPercent: -depth * 100 },
          {
            yPercent: depth * 100,
            ease: 'none',
            scrollTrigger: {
              trigger: el,
              start: 'top bottom',
              end: 'bottom top',
              scrub: true,
              invalidateOnRefresh: true
            }
          }
        )
      );
    });

    return function () {
      tweens.forEach(function (t) {
        if (t.scrollTrigger) t.scrollTrigger.kill();
        t.kill();
      });
      gsap.set(layers, { clearProps: 'transform' });
    };
  });

  /* ----------------------------------------------------------------------
     pin-scenes — the image holds, the words advance.

     Forced on touch too, at the author's explicit request (2026-09-08) —
     same accepted risk as h-scroll above: pin can read as a stuck swipe on
     some phones. If that report comes back, restore the `touch` guard below
     (static stacked scenes), don't patch around the symptom.
     ---------------------------------------------------------------------- */

  Motion.define('pin-scenes', function (el) {
    var scenes = el.querySelectorAll('[data-scene]');
    if (scenes.length < 2) return;

    if (!Motion.full() || !hasScrollTrigger) {
      el.setAttribute('data-scenes-static', 'true');
      return;
    }

    var gsap = window.gsap;
    var media = el.querySelectorAll('[data-scene-media]');

    gsap.set(scenes, { autoAlpha: 0, y: 24 });
    gsap.set(scenes[0], { autoAlpha: 1, y: 0 });
    if (media.length) {
      gsap.set(media, { autoAlpha: 0 });
      gsap.set(media[0], { autoAlpha: 1 });
    }

    var tl = gsap.timeline({
      scrollTrigger: {
        trigger: el,
        start: 'top top',
        // 110% por escena, no 90%: el relevo dejo de solaparse, asi que cada
        // transicion ocupa mas linea de tiempo. Con la distancia anterior el
        // tiempo de lectura de cada escena se comia el que gana el cruce.
        end: '+=' + scenes.length * 110 + '%',
        pin: true,
        scrub: 0.5,
        anticipatePin: 1,
        invalidateOnRefresh: true
      }
    });

    for (var i = 1; i < scenes.length; i++) {
      // La frase que sale se apaga entera ANTES de que entre la siguiente.
      //
      // Antes las dos corrian a la vez ('<'), asi que en el punto medio del
      // cruce se leian dos parrafos de display en italica superpuestos al 50%
      // cada uno: ilegible. El texto es lo unico que no se cruza. Las fotos si
      // siguen cruzandose debajo, porque dos imagenes mezcladas siguen siendo
      // una imagen, y el corte seco entre ellas se notaria mas que el cruce.
      tl.to(scenes[i - 1], { autoAlpha: 0, y: -24, duration: 0.3 }, '+=0.6')
        .to(scenes[i], { autoAlpha: 1, y: 0, duration: 0.4 }, '>');
      if (media[i]) {
        tl.to(media[i - 1], { autoAlpha: 0, duration: 0.7 }, '<-0.3')
          .to(media[i], { autoAlpha: 1, duration: 0.7 }, '<');
      }
    }

    return function () {
      if (tl.scrollTrigger) tl.scrollTrigger.kill();
      tl.kill();
      gsap.set(scenes, { clearProps: 'all' });
      if (media.length) gsap.set(media, { clearProps: 'all' });
    };
  });

  /* ----------------------------------------------------------------------
     sticky-cta — the buy bar appears once the reader is invested and hides
     again over the footer, where the real buttons live. Retention converted.
     ---------------------------------------------------------------------- */

  Motion.define('sticky-cta', function (el) {
    var afterSel = el.dataset.ctaAfter;
    var hideSel = el.dataset.ctaHideAt;
    var after = afterSel ? document.querySelector(afterSel) : null;

    if (!hasScrollTrigger || !Motion.enabled()) return;

    var triggers = [];
    var past = false;
    var atEnd = false;

    function sync() {
      el.dataset.visible = String(past && !atEnd);
    }

    if (after) {
      triggers.push(
        window.ScrollTrigger.create({
          trigger: after,
          start: 'bottom 80%',
          onEnter: function () { past = true; sync(); },
          onLeaveBack: function () { past = false; sync(); }
        })
      );
    } else {
      // The merchant deleted or renamed the section this was anchored to. Do not
      // silently lose the only buy button on the page — fall back to scroll
      // depth, which is a worse trigger but still a working one.
      // No trigger element + a numeric start = an absolute scroll position,
      // recomputed on refresh so resizing does not strand the bar.
      triggers.push(
        window.ScrollTrigger.create({
          start: function () {
            return window.ScrollTrigger.maxScroll(window) * 0.35;
          },
          invalidateOnRefresh: true,
          onEnter: function () { past = true; sync(); },
          onLeaveBack: function () { past = false; sync(); }
        })
      );
    }

    if (hideSel) {
      var hide = document.querySelector(hideSel);
      if (hide) {
        triggers.push(
          window.ScrollTrigger.create({
            trigger: hide,
            start: 'top 90%',
            onEnter: function () { atEnd = true; sync(); },
            onLeaveBack: function () { atEnd = false; sync(); }
          })
        );
      }
    }

    // Derive state from position, not only from crossings. A reload restores the
    // previous scroll offset, so onEnter never fires and the bar would stay
    // hidden for a reader who is already deep in the page.
    function syncFromPosition() {
      var y = window.scrollY;
      past = triggers.length > 0 && y >= triggers[0].start;
      atEnd = triggers.length > 1 && y >= triggers[1].start;
      sync();
    }

    window.ScrollTrigger.addEventListener('refresh', syncFromPosition);
    syncFromPosition();

    return function () {
      window.ScrollTrigger.removeEventListener('refresh', syncFromPosition);
      triggers.forEach(function (t) { t.kill(); });
      el.dataset.visible = 'false';
    };
  });

  /* ----------------------------------------------------------------------
     cover-tilt — el objeto que se vende, tratado como objeto.

     Tres cosas sobre la portada: una inclinación ligada al scroll, otra que
     sigue al puntero, y un barrido de luz dorada la primera vez que entra.

     Escribe sobre .cover__tilt y nunca sobre .cover__flip, porque ese nodo ya
     tiene su propio transform en CSS para el giro de la contraportada. Dos
     dueños de la misma propiedad y la portada se queda trabada de medio lado.

     El seguimiento del puntero se omite en pantallas táctiles: sin puntero que
     seguir, el listener solo gastaría batería.
     ---------------------------------------------------------------------- */

  Motion.define('cover-tilt', function (el) {
    if (!Motion.full() || !hasScrollTrigger) return;

    var gsap = window.gsap;
    var flip = el.querySelector('[data-cover-flip]');
    var sheen = el.querySelector('.cover__sheen');
    var tweens = [];
    var pointerBound = false;

    var quickX = gsap.quickTo(el, 'rotationX', { duration: 0.6, ease: 'power3.out' });
    var quickY = gsap.quickTo(el, 'rotationY', { duration: 0.6, ease: 'power3.out' });

    // Deriva mientras la sección cruza la pantalla. Muy poca: la portada tiene
    // que leerse, no bailar.
    tweens.push(
      gsap.fromTo(
        el,
        { rotationX: 7, rotationY: -18, y: 18 },
        {
          rotationX: -5,
          rotationY: -14,
          y: -18,
          ease: 'none',
          scrollTrigger: {
            trigger: el,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1,
            invalidateOnRefresh: true
          }
        }
      )
    );

    // El barrido dorado corre una sola vez, al entrar. En bucle convertiría
    // una portada en un banner.
    if (sheen) {
      tweens.push(
        gsap.fromTo(
          sheen,
          { xPercent: -140, opacity: 0 },
          {
            xPercent: 140,
            opacity: 1,
            duration: 1.5,
            ease: 'power2.inOut',
            scrollTrigger: { trigger: el, start: 'top 78%', once: true },
            onComplete: function () { gsap.set(sheen, { opacity: 0 }); }
          }
        )
      );
    }

    function onMove(e) {
      var r = el.getBoundingClientRect();
      var px = (e.clientX - r.left) / r.width - 0.5;
      var py = (e.clientY - r.top) / r.height - 0.5;
      // Girada, la cara visible es la de atrás: invertir el eje mantiene la
      // inclinación del lado donde está el puntero.
      var flipped = flip && flip.classList.contains('is-flipped');
      var rest = flipped ? 16 : -16;
      quickY(gsap.utils.clamp(rest - 6, rest + 6, rest + px * 12));
      quickX(gsap.utils.clamp(-8, 8, -py * 16));
    }

    function onLeave() {
      var flipped = flip && flip.classList.contains('is-flipped');
      quickY(flipped ? 16 : -16);
      quickX(0);
    }

    var finePointer =
      window.matchMedia('(hover: hover)').matches &&
      window.matchMedia('(pointer: fine)').matches;

    if (finePointer) {
      el.addEventListener('pointermove', onMove);
      el.addEventListener('pointerleave', onLeave);
      pointerBound = true;
    }

    return function () {
      if (pointerBound) {
        el.removeEventListener('pointermove', onMove);
        el.removeEventListener('pointerleave', onLeave);
      }
      tweens.forEach(function (t) {
        if (t.scrollTrigger) t.scrollTrigger.kill();
        t.kill();
      });
      gsap.set(el, { clearProps: 'transform' });
      if (sheen) gsap.set(sheen, { clearProps: 'transform,opacity' });
    };
  });

  /* ----------------------------------------------------------------------
     reel-mask — los clips se descubren de abajo hacia arriba, en cascada.

     Va en clip-path y no en opacity porque estos tiles son verticales y llenos
     de imagen: un fundido los deja grises a media animación, mientras que la
     máscara mantiene el color desde el primer cuadro.

     Sustituye al reveal normal en esta sección, así que los <li> no llevan
     data-reveal: si lo llevaran correrían las dos animaciones sobre el mismo
     nodo y la cascada saldría a destiempo.
     ---------------------------------------------------------------------- */

  Motion.define('reel-mask', function (el) {
    var items = el.querySelectorAll('[data-reel-item]');
    if (!items.length) return;
    if (!Motion.full() || !hasScrollTrigger) return;

    var gsap = window.gsap;
    var tween = gsap.fromTo(
      items,
      { clipPath: 'inset(100% 0% 0% 0%)', y: 26 },
      {
        clipPath: 'inset(0% 0% 0% 0%)',
        y: 0,
        duration: 1,
        ease: 'power3.out',
        stagger: 0.12,
        scrollTrigger: { trigger: el, start: 'top 82%', once: true }
      }
    );

    return function () {
      if (tween.scrollTrigger) tween.scrollTrigger.kill();
      tween.kill();
      gsap.set(items, { clearProps: 'clipPath,transform' });
    };
  });

  /* ----------------------------------------------------------------------
     scroll-cue — la señal de «desliza» al pie del hero.

     Corre sobre ScrollMagic 2.0.8, pedido explícitamente. Es el único efecto
     del tema que no usa ScrollTrigger.

     Lo que hay que saber para mantenerlo:

     - No se carga el plugin `animation.gsap.js` de ScrollMagic. Ese plugin
       espera TweenMax, o sea GSAP 2, y aquí corre GSAP 3.13, que no expone ese
       global. El puente correcto sin plugin es una timeline en pausa cuyo
       progreso se fija desde el evento `progress` de la escena: ScrollMagic
       decide cuánto, GSAP interpola.
     - El controlador es uno por página y se comparte. Se crea con la primera
       escena y se destruye con la última, con un contador — el editor de temas
       monta y desmonta secciones a cada edición, y un controlador huérfano
       deja su bucle corriendo sobre un DOM que ya no existe.
     - Si el archivo no carga, no hay desvanecido: la señal se queda visible.
       La chispa que baja por el riel es CSS y sigue corriendo igual, así que
       la degradación es que sobra una señal, no que falte movimiento.
     ---------------------------------------------------------------------- */

  var smController = null;
  var smScenes = 0;

  function acquireController() {
    if (!smController) smController = new window.ScrollMagic.Controller();
    smScenes++;
    return smController;
  }

  function releaseController() {
    smScenes--;
    if (smScenes <= 0 && smController) {
      smController.destroy(true);
      smController = null;
      smScenes = 0;
    }
  }

  Motion.define('scroll-cue', function (el) {
    var parts = el.children;
    if (!parts.length) return;
    if (!Motion.enabled() || !hasGsap) return;
    if (typeof window.ScrollMagic === 'undefined') return;

    var gsap = window.gsap;
    // El hero manda el recorrido. Fuera de una sección (la vista previa local)
    // el propio elemento sirve de disparador.
    var trigger = el.closest('[data-section-id]') || el;

    // Se anima a los hijos, no al elemento: `reveal` ya escribe opacity e y
    // sobre este mismo <p> para su entrada, y dos animaciones sobre la misma
    // propiedad se pisan a cada cuadro.
    var tl = gsap.timeline({ paused: true }).to(parts, {
      opacity: 0,
      y: 14,
      ease: 'none',
      duration: 1
    });

    var controller = acquireController();
    var scene = new window.ScrollMagic.Scene({
      triggerElement: trigger,
      triggerHook: 0,
      duration: '35%'
    }).addTo(controller);

    scene.on('progress', function (e) {
      tl.progress(e.progress);
    });

    return function () {
      scene.remove();
      scene.destroy(true);
      tl.kill();
      gsap.set(parts, { clearProps: 'opacity,transform' });
      releaseController();
    };
  });

  /* ----------------------------------------------------------------------
     water-veil — el hilo visual entre secciones. Un único elemento fijo
     (assets/base.css .water-veil, montado en layout/theme.liquid) que pasa
     por encima de cada frontera de sección como una lámina de agua: sube y
     baja en campana justo donde una sección entrega a la siguiente. Misma
     idea que water-current (una sola --custom-property por scroll), pero a
     nivel de página en vez de una sección.

     Se salta las fronteras que tocan una sección con pin (pin-scenes,
     h-scroll, sticky-stack, sequence): esas ya son dueñas de su tramo de
     scroll y el velo encima competiría con su propia coreografía.
     ---------------------------------------------------------------------- */

  Motion.define('water-veil', function (el) {
    if (!Motion.full() || !hasScrollTrigger) {
      el.style.setProperty('--veil', '0');
      return;
    }

    var PIN_OWNED = /(^|\s)(pin-scenes|h-scroll|sticky-stack|sequence|immersion)(\s|$)/;

    function isPinOwned(section) {
      if (!section) return true; // sin sección de un lado: no se adivina, se salta.
      var nodes = section.querySelectorAll('[data-motion-behaviour]');
      for (var i = 0; i < nodes.length; i++) {
        if (PIN_OWNED.test(nodes[i].dataset.motionBehaviour || '')) return true;
      }
      return false;
    }

    var sections = Array.prototype.slice.call(document.querySelectorAll('main [data-section-id]'));
    var triggers = [];

    for (var i = 1; i < sections.length; i++) {
      var prev = sections[i - 1];
      var next = sections[i];
      if (isPinOwned(prev) || isPinOwned(next)) continue;

      triggers.push(window.ScrollTrigger.create({
        trigger: next,
        start: 'top 60%',
        end: 'top 40%',
        scrub: true,
        onUpdate: function (self) {
          // Campana: el velo aparece y desaparece cruzando la frontera.
          var p = 1 - Math.abs(self.progress - 0.5) * 2;
          el.style.setProperty('--veil', p.toFixed(3));
        }
      }));
    }

    return function () {
      for (var i = 0; i < triggers.length; i++) triggers[i].kill();
    };
  });

  /* ----------------------------------------------------------------------
     hero-scroll-text — "el texto va saliendo a medida que se hace scroll en
     el video" (autor, 2026-09-08). NO es un parallax: el texto no se
     desplaza ni se desvanece al salir. Cada línea entra en su propia
     ventana de progreso de scroll, escalonada, sobre el MISMO rango que
     scrubea la secuencia de frames del hero — scrolleás, el video avanza y
     el texto se va escribiendo encima.

     Reemplaza al behaviour 'reveal' en este hero: 'reveal' dispara un tween
     en tiempo real al cruzar un borde (aparece todo junto y se queda), que
     es justo lo que el autor rechazó. Acá el progreso del texto ES la
     posición de scroll, sin easing de catch-up (scrub: true), igual que la
     secuencia — si parás de scrollear, el texto para.

     Sin GSAP / motion off / reduced: se estampa data-revealed en todo y el
     texto queda visible y quieto. Nunca se deja texto en opacity 0.
     ---------------------------------------------------------------------- */

  Motion.define('hero-scroll-text', function (el) {
    var targets = el.querySelectorAll('[data-reveal], .hero__title');

    if (!targets.length) return;

    // El estado de reposo de [data-reveal] es opacity 0 hasta que algo
    // estampa data-revealed (assets/base.css). Se estampa YA en todos: de
    // acá en adelante la opacidad la maneja GSAP, no el CSS.
    for (var i = 0; i < targets.length; i++) targets[i].setAttribute('data-revealed', '');

    if (!Motion.full() || !hasScrollTrigger) return;

    var gsap = window.gsap;

    // La identidad (eyebrow, runas, título) NO se escrubea: si todo el texto
    // arranca en opacity 0, quien no scrollea ve una foto sin título ni
    // botón de compra, y el título deja de ser el primer texto que pinta.
    // Todo hasta el <h1> inclusive queda visible al cargar; lo que sigue
    // (subtítulo, línea informativa, CTA, pista de scroll) es lo que se va
    // escribiendo con el scroll.
    var list = Array.prototype.slice.call(targets);
    var titleAt = -1;
    for (var k = 0; k < list.length; k++) {
      if (list[k].classList.contains('hero__title')) { titleAt = k; break; }
    }
    // Los botones tampoco se escrubean. Con el CTA en opacity 0 hasta el 75%
    // del hero, el botón de compra no existe al cargar la página y la barra
    // sticky no cubre ese tramo (aparece después de #fragmento). El CTA no es
    // texto narrativo: es la acción. Se escriben el subtítulo, la línea
    // informativa y la pista de scroll.
    var scrolled = list.slice(titleAt + 1).filter(function (node) {
      return !node.classList.contains('hero__cta');
    });
    if (!scrolled.length) return;

    var tl = gsap.timeline({
      defaults: { ease: 'none' },
      scrollTrigger: {
        trigger: el.closest('.hero') || el,
        start: 'top top',
        end: 'bottom top',
        scrub: true,
        invalidateOnRefresh: true
      }
    });

    // Cada línea ocupa una fracción del recorrido y entra un poco antes de
    // que termine la anterior: se lee como una frase que se va escribiendo,
    // no como bloques separados. Se reparte sobre el 70% inicial del hero
    // para que la última línea termine de entrar antes de que la sección
    // empiece a salir de pantalla.
    var span = 0.7 / scrolled.length;
    for (var j = 0; j < scrolled.length; j++) {
      tl.fromTo(
        scrolled[j],
        { opacity: 0, y: '1.25rem' },
        { opacity: 1, y: '0rem', duration: span * 0.9 },
        j * span
      );
    }

    return function () {
      if (tl.scrollTrigger) tl.scrollTrigger.kill();
      tl.kill();
      gsap.set(targets, { clearProps: 'opacity,transform' });
    };
  });

  /* ----------------------------------------------------------------------
     hero-exit — el hero se hunde al salir: zoom leve + oscurecido, entrega
     el relevo a la escena de inmersión que sigue. --sink 0 (en pantalla) ->
     1 (fuera). Solo aplica al hero con water_video_bg (assets/base.css).
     ---------------------------------------------------------------------- */

  Motion.define('hero-exit', function (el) {
    if (!Motion.full() || !hasScrollTrigger) {
      el.style.setProperty('--sink', '0');
      return;
    }

    var st = window.ScrollTrigger.create({
      trigger: el,
      start: 'top top',
      end: 'bottom top',
      scrub: true,
      onUpdate: function (self) {
        el.style.setProperty('--sink', self.progress.toFixed(3));
      }
    });

    return function () { st.kill(); };
  });

  /* ----------------------------------------------------------------------
     immersion — "te hundes y resurges" (HERO-BRIEF.md escena 1). Sección
     pinned de 1.5 viewports: el video de agua (burbujas subiendo, rayos de
     sol) sube de opacidad en la primera mitad del scroll (te hundes) y baja
     en la segunda (resurges), justo cuando la cita siguiente ya está
     entrando por su propio 'reveal' — el agua entrega el relevo al texto en
     vez de cortar seco.

     El video hace loop en autoplay por sus propios atributos <video>; esta
     behaviour solo controla opacidad, no scrubbea currentTime — más simple
     y sin el stutter de seek-por-frame en un clip comprimido.
     ---------------------------------------------------------------------- */

  Motion.define('immersion', function (el) {
    if (!Motion.full() || !hasScrollTrigger) {
      // Reduced/off: nunca se bloquea el scroll. Queda como un bloque de
      // video normal a opacidad plena, igual que cualquier otro fondo.
      el.style.setProperty('--immersion', '1');
      el.style.setProperty('--immersion-feather', '0');
      return;
    }

    /* Dos tramos (2026-09-23, "la transición entre el primer y segundo
       componente no se siente armónica"). Antes la campana 0 -> 1 -> 0 corría
       solo DENTRO del pin: mientras el hero salía, esta sección se veía como
       un viewport entero de tinta vacía y el agua recién aparecía cuando ya
       estaba fija. Ahora:
       1. Entrada ('top bottom' -> 'top top'): te hundes MIENTRAS el mar del
          hero se va. Opacidad 0 -> 1 y el borde superior del video llega
          difuminado (--immersion-feather 60 -> 0 %) para no dibujar una línea
          contra el hero.
       2. Pin (+150%): primera mitad bajo el agua con el titular, segunda
          mitad resurges (1 -> 0) y entra la cita siguiente. */
    var entering = true;

    // Difuminado del borde de arriba: 60 % -> 0 con raíz cuadrada, para que
    // siga ancho casi hasta el final de la entrada (lineal quedaba en ~70 px
    // justo cuando la superficie iluminada del video toca el hero oscuro).
    function feather(p) { return 60 * Math.sqrt(Math.max(0, 1 - p)); }

    function paint(o, feather) {
      el.style.setProperty('--immersion', o.toFixed(3));
      el.style.setProperty('--immersion-feather', feather.toFixed(1));
    }

    var inST = window.ScrollTrigger.create({
      trigger: el,
      start: 'top bottom',
      end: 'top top',
      scrub: true,
      onUpdate: function (self) {
        if (!entering) return;
        paint(self.progress, feather(self.progress));
      }
    });

    var st = window.ScrollTrigger.create({
      trigger: el,
      start: 'top top',
      end: '+=150%',
      pin: true,
      scrub: true,
      onUpdate: function (self) {
        var p = self.progress;
        entering = p === 0;
        paint(p < 0.5 ? 1 : 1 - (p - 0.5) / 0.5, 0);
      }
    });

    // Sembrar YA desde el estado real (entrar a mitad de página, recarga con
    // scroll restaurado): onUpdate no corre hasta que el trigger se mueve.
    if (st.progress > 0) {
      entering = false;
      paint(st.progress < 0.5 ? 1 : 1 - (st.progress - 0.5) / 0.5, 0);
    } else {
      paint(inST.progress, feather(inST.progress));
    }

    return function () { inST.kill(); st.kill(); };
  });

  /* ----------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */

  function boot() {
    Motion.ready = true;
    Motion.mount(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor lifecycle. Without these the editor accumulates dead
  // triggers on every merchant keystroke and the preview degrades.
  document.addEventListener('shopify:section:load', function (e) {
    var id = e.detail.sectionId;
    Motion.unmount(id);
    var section = e.target.querySelector('[data-section-id]') || e.target;
    // The editor replaces the markup wholesale, so the new nodes carry no
    // flags. Clearing is belt-and-braces for partial re-renders.
    var mounted = section.querySelectorAll('[data-motion-behaviour]');
    for (var i = 0; i < mounted.length; i++) mounted[i].__motionMounted = null;
    Motion.mount(section);
    if (hasScrollTrigger) window.ScrollTrigger.refresh();
  });

  document.addEventListener('shopify:section:unload', function (e) {
    Motion.unmount(e.detail.sectionId);
    if (hasScrollTrigger) window.ScrollTrigger.refresh();
  });

  window.Motion = Motion;
})();
